import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { NgxSpinnerService } from 'ngx-spinner';
import { DepartureCheckService } from 'src/app/services/departure-check.service';
import { UtilsService } from 'src/app/services/utils.service';
import { environment } from 'src/environments/environment';

const FUNCIONAMIENTO_LABELS: { key: string, label: string, icon: string }[] = [
    { key: 'keys_ok', label: 'Llaves', icon: 'icon-key.svg' },
    { key: 'headlights_ok', label: 'Faros', icon: 'icon-headlights.svg' },
    { key: 'ignition_ok', label: 'Encendedor', icon: 'icon-ignition.svg' },
    { key: 'levers_ok', label: 'Palancas', icon: 'icon-levers.svg' },
    { key: 'tires_ok', label: 'Neumáticos', icon: 'icon-tires.svg' },
    { key: 'fire_extinguisher_ok', label: 'Extintor', icon: 'icon-fire-extinguisher.svg' },
    { key: 'upholstery_ok', label: 'Tapizados', icon: 'icon-upholstery.svg' },
    { key: 'mirrors_ok', label: 'Espejos', icon: 'icon-mirrors.svg' },
    { key: 'accessories_ok', label: 'Accesorios', icon: 'icon-accessories.svg' },
];

interface SensorItem {
    key: string;
    label: string;
    icon: string;
    w: number;
    h: number;
    active: boolean;
}

interface DocumentoItem {
    key: string;
    label: string;
    subtitle: string;
    icon: string;
    boxClass: string;
    verified: boolean;
}

const SENSORES_ROW1: SensorItem[] = [
    { key: 'cinturon', label: 'Cinturón de seguridad', icon: 'icon-sensor-seatbelt.svg', w: 50, h: 55, active: false },
    { key: 'motor', label: 'Motor', icon: 'icon-sensor-engine.svg', w: 67, h: 48.418, active: false },
    { key: 'abs', label: 'ABS', icon: 'icon-sensor-abs.svg', w: 64, h: 48, active: false },
    { key: 'aceite', label: 'Aceite', icon: 'icon-sensor-oil.svg', w: 81.4789, h: 34, active: false },
    { key: 'bateria', label: 'Batería', icon: 'icon-sensor-battery.svg', w: 56.4122, h: 37, active: false },
    { key: 'airbag', label: 'Airbag', icon: 'icon-sensor-airbag.svg', w: 38, h: 44, active: false },
    { key: 'alerta', label: 'Alerta general', icon: 'icon-sensor-warning.svg', w: 59.6, h: 44, active: false },
];
const SENSORES_ROW2: SensorItem[] = [
    { key: 'luces', label: 'Luces', icon: 'icon-sensor-headlight.svg', w: 55.3901, h: 41.9996, active: false },
    { key: 'asientos', label: 'Sensor de asiento', icon: 'icon-sensor-seat.svg', w: 43.4983, h: 55.0005, active: false },
    { key: 'limpiaparabrisas', label: 'Limpiaparabrisas', icon: 'icon-sensor-wash.svg', w: 51, h: 43.8145, active: false },
];

/** Orden fijo del diseño: lateral izquierda, lateral derecha, frontal, trasera. */
const FOTO_ORDER: string[] = ['lateral_izquierda', 'lateral_derecha', 'frontal', 'trasera'];

const FOTO_LABELS: Record<string, string> = {
    frontal: 'Frontal',
    lateral_izquierda: 'Lateral izquierda',
    lateral_derecha: 'Lateral derecha',
    trasera: 'Trasera',
    tablero: 'Tablero',
    danos: 'Daños registrados',
    adicional: 'Adicional',
};

@Component({
    selector: 'app-departure-check-summary',
    templateUrl: './departure-check-summary.component.html',
    styleUrls: ['./departure-check-summary.component.css']
})
export class DepartureCheckSummaryComponent implements OnInit {
    check: any = null;
    loading = false;
    funcionamientoLabels = FUNCIONAMIENTO_LABELS;
    sensoresRow1: SensorItem[] = SENSORES_ROW1.map(s => ({ ...s }));
    sensoresRow2: SensorItem[] = SENSORES_ROW2.map(s => ({ ...s }));
    documentos: DocumentoItem[] = [];
    fotoLabels = FOTO_LABELS;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private location: Location,
        private departureCheckService: DepartureCheckService,
        private spinner: NgxSpinnerService,
        private utils: UtilsService
    ) { }

    ngOnInit(): void {
        const id = Number(this.route.snapshot.paramMap.get('id'));
        this.loading = true;
        this.spinner.show();
        this.departureCheckService.getCheck(id).subscribe({
            next: (resp: any) => {
                this.check = resp;
                const activeSensors: string[] = resp.dashboard_sensors || [];
                this.sensoresRow1.forEach(s => s.active = activeSensors.includes(s.key));
                this.sensoresRow2.forEach(s => s.active = activeSensors.includes(s.key));

                const svc = resp.service || {};
                this.documentos = [
                    { key: 'insurance_verified', label: 'Póliza de seguro (Impresa)', subtitle: svc.insurer || '--', icon: 'icon-shield-check.svg', boxClass: 'dc-doc-box--teal', verified: !!resp.documentos?.insurance_verified },
                    { key: 'id_verified', label: 'Identificación oficial (Operador)', subtitle: svc.driver_name || '--', icon: 'icon-id-card-official.svg', boxClass: 'dc-doc-box--blue', verified: !!resp.documentos?.id_verified },
                    { key: 'license_verified', label: 'Licencia de conducir (Operador)', subtitle: svc.driver_name || '--', icon: 'icon-id-card-license.svg', boxClass: 'dc-doc-box--orange', verified: !!resp.documentos?.license_verified },
                    { key: 'registration_verified', label: 'Tarjeta de circulación (Unidad)', subtitle: (svc.unit || '') + (svc.unit_configuration ? ' (' + svc.unit_configuration + ')' : ''), icon: 'icon-id-card-registration.svg', boxClass: 'dc-doc-box--purple', verified: !!resp.documentos?.registration_verified },
                ];

                this.loading = false;
                this.spinner.hide();
            },
            error: () => {
                this.loading = false;
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Error', text: 'No se pudo cargar el resumen del check', icon: 'error' });
                this.router.navigateByUrl('/unit/departure-checks');
            }
        });
    }

    goBack(): void {
        this.location.back();
    }

    downloadPdf(): void {
        if (!this.check) return;
        this.spinner.show();
        this.departureCheckService.downloadPdf(this.check.id).subscribe({
            next: (blob: Blob) => {
                this.spinner.hide();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `check-salida-${this.check.id}.pdf`;
                a.click();
                window.URL.revokeObjectURL(url);
            },
            error: () => {
                this.spinner.hide();
                this.utils.alertSwal({ title: 'Error', text: 'No se pudo descargar el PDF', icon: 'error' });
            }
        });
    }

    funcionamientoValue(key: string): boolean {
        return !!this.check?.funcionamiento?.[key];
    }

    get funcionamientoOkCount(): number {
        if (!this.check) return 0;
        return this.funcionamientoLabels.filter(f => this.funcionamientoValue(f.key)).length;
    }

    photoUrl(category: string): string | null {
        const photo = (this.check?.photos || []).find((p: any) => p.category === category);
        return photo ? environment.url_page + photo.url : null;
    }

    fotoLabel(category: string): string {
        return this.fotoLabels[category] || category;
    }

    get evidenciaFotos(): any[] {
        const fotos = (this.check?.photos || []).filter((p: any) => p.category !== 'odometro');
        return [...fotos].sort((a, b) => {
            const ia = FOTO_ORDER.indexOf(a.category);
            const ib = FOTO_ORDER.indexOf(b.category);
            return (ia === -1 ? FOTO_ORDER.length : ia) - (ib === -1 ? FOTO_ORDER.length : ib);
        });
    }

    get documentosCompletas(): boolean {
        return this.documentos.length > 0 && this.documentos.every(d => d.verified);
    }

    get needleAngle(): number {
        return ((this.check?.fuel_level ?? 0) / 100) * 180 - 90;
    }
}
